import { type TimeBasedComparisonProps, type WeekData } from "../../../types/analytics"
import dayjs from "dayjs"

export const TimeBasedComparison: React.FC<TimeBasedComparisonProps> = ({
  analyticsItems,
  showOverlay = false,
}) => {
  const weeks: WeekData[] = []
  const today = dayjs()

  for (let weekNumber = 0; weekNumber < 4; weekNumber++) {
    const weekStart = today.subtract(weekNumber * 7, "day").startOf("week")
    const weekEnd = weekStart.endOf("week")

    const weekData = analyticsItems.filter((item) => {
      const itemDate = dayjs(item.date)
      return (
        (itemDate.isAfter(weekStart) || itemDate.isSame(weekStart)) &&
        (itemDate.isBefore(weekEnd) || itemDate.isSame(weekEnd))
      )
    })

    const hasData = weekData.length > 0

    const uniqueUsers = weekData.reduce((sum, item) => sum + item.unique_users, 0)
    const vtoEvents = weekData.reduce((sum, item) => sum + item.vto_events, 0)
    const addToCartEvents = weekData.reduce((sum, item) => sum + item.add_to_cart, 0)

    let label = ""
    if (weekNumber === 0) label = "This Week"
    else if (weekNumber === 1) label = "Last Week"
    else label = `${weekNumber} Weeks Ago`

    weeks.push({
      weekNumber,
      label,
      data: {
        uniqueUsers,
        vtoEvents,
        addToCartEvents,
      },
      hasData,
    })
  }

  const weeklyData = weeks

  const calculateChange = (
    current: number,
    previous: number
  ): { value: number; trend: "up" | "down" | "stable" } => {
    if (previous === 0) return { value: 0, trend: "stable" }

    const change = ((current - previous) / previous) * 100
    let trend: "up" | "down" | "stable" = "stable"

    if (change > 1) trend = "up"
    else if (change < -1) trend = "down"

    return { value: Math.abs(change), trend }
  }

  if (showOverlay)
    return (
      <div className="p-4 bg-card rounded border">
        <h3 className="mb-3 text-lg font-semibold tracking-tight text-foreground">
          Weekly Comparison - Loading...
        </h3>
        <div className="grid grid-cols-4 gap-4">
          {Array.from({ length: 12 }).map((_, index) => (
            <div key={index} className="p-3 bg-muted rounded h-[120px]" />
          ))}
        </div>
      </div>
    )

  return (
    <div className="p-6 bg-card rounded-lg border">
      <div className="flex flex-col gap-6">
        <div>
          <h3 className="text-lg font-semibold tracking-tight text-foreground">
            Week-by-Week Comparisons
          </h3>
          <p className="text-sm text-muted-foreground">
            Performance comparison across available weeks (4 weeks with data)
          </p>
        </div>
        <div className="grid grid-cols-3 gap-6">
          <div className="bg-muted rounded-lg border p-6">
            <h4 className="mb-4 text-base font-semibold tracking-tight text-foreground">
              Unique Users
            </h4>
            <div className="space-y-4">
              {weeklyData.map((week, index) => {
                const previousWeek = weeklyData[index + 1]
                const change = previousWeek
                  ? calculateChange(week.data.uniqueUsers, previousWeek.data.uniqueUsers)
                  : { value: 0, trend: "stable" as const }

                return (
                  <div key={week.weekNumber} className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">{week.label}</div>
                      <div className="text-xl font-semibold tracking-tight text-foreground">
                        {week.data.uniqueUsers}
                      </div>
                    </div>
                    {previousWeek && week.hasData && previousWeek.hasData && (
                      <div
                        className={`text-xs font-medium ${
                          change.trend === "up"
                            ? "analytics-trend-up"
                            : change.trend === "down"
                            ? "analytics-trend-down"
                            : "analytics-trend-stable"
                        }`}
                      >
                        {change.trend === "up" ? "↗" : change.trend === "down" ? "↘" : "→"}{" "}
                        {change.value.toFixed(0)}%
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          <div className="bg-muted rounded-lg border p-6">
            <h4 className="mb-4 text-base font-semibold tracking-tight text-foreground">
              Amount of Tried On events
            </h4>
            <div className="space-y-4">
              {weeklyData.map((week, index) => {
                const previousWeek = weeklyData[index + 1]
                const change = previousWeek
                  ? calculateChange(week.data.vtoEvents, previousWeek.data.vtoEvents)
                  : { value: 0, trend: "stable" as const }

                return (
                  <div key={week.weekNumber} className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">{week.label}</div>
                      <div className="text-xl font-semibold tracking-tight text-foreground">
                        {week.data.vtoEvents}
                      </div>
                    </div>
                    {previousWeek && week.hasData && previousWeek.hasData && (
                      <div
                        className={`text-xs font-medium ${
                          change.trend === "up"
                            ? "analytics-trend-up"
                            : change.trend === "down"
                            ? "analytics-trend-down"
                            : "analytics-trend-stable"
                        }`}
                      >
                        {change.trend === "up" ? "↗" : change.trend === "down" ? "↘" : "→"}{" "}
                        {change.value.toFixed(0)}%
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          <div className="bg-muted rounded-lg border p-6">
            <h4 className="mb-4 text-base font-semibold tracking-tight text-foreground">
              Cart Events
            </h4>
            <div className="space-y-4">
              {weeklyData.map((week, index) => {
                const previousWeek = weeklyData[index + 1]
                const change = previousWeek
                  ? calculateChange(week.data.addToCartEvents, previousWeek.data.addToCartEvents)
                  : { value: 0, trend: "stable" as const }

                return (
                  <div key={week.weekNumber} className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">{week.label}</div>
                      <div className="text-xl font-semibold tracking-tight text-foreground">
                        {week.data.addToCartEvents}
                      </div>
                    </div>
                    {previousWeek && week.hasData && previousWeek.hasData && (
                      <div
                        className={`text-xs font-medium ${
                          change.trend === "up"
                            ? "analytics-trend-up"
                            : change.trend === "down"
                            ? "analytics-trend-down"
                            : "analytics-trend-stable"
                        }`}
                      >
                        {change.trend === "up" ? "↗" : change.trend === "down" ? "↘" : "→"}{" "}
                        {change.value.toFixed(0)}%
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
